import React from 'react';
import styled from 'styled-components';
import { motion, useMotionValue, useTransform } from 'framer-motion';

const CardWrapper = styled(motion.div)`
  position: absolute;
  inset: 0;
  border-radius: 24px;
  overflow: hidden;
  cursor: grab;
  user-select: none;
  will-change: transform;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);

  &:active {
    cursor: grabbing;
  }
`;

const CardImage = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${props => props.$src});
  background-size: cover;
  background-position: center;
  pointer-events: none;
`;

const CardOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 40%, rgba(0, 0, 0, 0.75) 100%);
  pointer-events: none;
`;

const CardContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px;
  color: white;
  pointer-events: none;
`;

const CardTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 4px 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
`;

const CardSubtitle = styled.span`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
`;

const IndicatorLabel = styled(motion.div)`
  position: absolute;
  top: 32px;
  font-size: 28px;
  font-weight: 800;
  padding: 8px 16px;
  border-radius: 12px;
  border: 3px solid;
  pointer-events: none;
  z-index: 5;
`;

const DRAG_THRESHOLD = 80;
const VELOCITY_THRESHOLD = 300;

const SwipeCard = ({ card, onSwipe, isTop = false, stackOffset = 0 }) => {
  const x = useMotionValue(0);

  const likeOpacity = useTransform(x, [0, DRAG_THRESHOLD], [0, 1]);
  const nopeOpacity = useTransform(x, [-DRAG_THRESHOLD, 0], [1, 0]);
  const rotate = useTransform(x, [-200, 0, 200], [-12, 0, 12]);
  const bgOverlay = useTransform(x, (v) => {
    const abs = Math.min(Math.abs(v) / DRAG_THRESHOLD, 1) * 0.35;
    return v > 0
      ? `rgba(34, 197, 94, ${abs})`
      : `rgba(239, 68, 68, ${abs})`;
  });

  const handleDragEnd = (_, info) => {
    const { offset, velocity } = info;
    if (
      offset.x > DRAG_THRESHOLD || velocity.x > VELOCITY_THRESHOLD
    ) {
      onSwipe('right');
    } else if (
      offset.x < -DRAG_THRESHOLD || velocity.x < -VELOCITY_THRESHOLD
    ) {
      onSwipe('left');
    }
  };

  return (
    <CardWrapper
      drag={isTop ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={1}
      onDragEnd={isTop ? handleDragEnd : undefined}
      style={{
        x: isTop ? x : 0,
        rotate: isTop ? rotate : 0,
        zIndex: 10 - stackOffset,
      }}
      animate={{
        scale: 1 - stackOffset * 0.05,
        y: stackOffset * 8,
      }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
    >
      <CardImage $src={card.image} />
      <CardOverlay />

      {/* Green/red tint overlay */}
      {isTop && (
        <motion.div
          style={{
            position: 'absolute',
            inset: 0,
            background: bgOverlay,
            pointerEvents: 'none',
            zIndex: 4,
          }}
        />
      )}

      <CardContent>
        <CardTitle>{card.title}</CardTitle>
        <CardSubtitle>{card.subtitle}</CardSubtitle>
      </CardContent>

      {/* LIKE label */}
      {isTop && (
        <IndicatorLabel
          style={{
            opacity: likeOpacity,
            left: 24,
            color: '#22c55e',
            borderColor: '#22c55e',
            transform: 'rotate(-12deg)',
          }}
        >
          LIKE
        </IndicatorLabel>
      )}

      {/* NOPE label */}
      {isTop && (
        <IndicatorLabel
          style={{
            opacity: nopeOpacity,
            right: 24,
            color: '#ef4444',
            borderColor: '#ef4444',
            transform: 'rotate(12deg)',
          }}
        >
          NOPE
        </IndicatorLabel>
      )}
    </CardWrapper>
  );
};

export default SwipeCard;
